import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ConversionRateResponse,
  ConvertCurrencyIdentifiers,
  ConvertCurrencyResponse,
  DecimalValue,
  GetConversionRateIdentifiers,
  ListCurrenciesResponse,
  convertCurrency as universalConvertCurrency,
  getConversionRate as universalGetConversionRate,
  listCurrencies as universalListCurrencies,
} from './ecommerce-currency-converter-v1-currency-rate-currencies.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function listCurrencies(
  httpClient: HttpClient
): ListCurrenciesSignature {
  return () =>
    universalListCurrencies(
      // @ts-ignore
      { httpClient }
    );
}

interface ListCurrenciesSignature {
  /**
   * Returns an array of currencies. The array lists all currencies for which Wix supports conversion and their symbols.
   */
  (): Promise<
    NonNullablePaths<
      ListCurrenciesResponse,
      | `currencies`
      | `currencies.${number}.code`
      | `currencies.${number}.symbol`,
      4
    >
  >;
}

export function convertCurrency(
  httpClient: HttpClient
): ConvertCurrencySignature {
  return (
    identifiers: NonNullablePaths<ConvertCurrencyIdentifiers, `from` | `to`, 2>,
    amounts: DecimalValue[]
  ) =>
    universalConvertCurrency(
      identifiers,
      amounts,
      // @ts-ignore
      { httpClient }
    );
}

interface ConvertCurrencySignature {
  /**
   * Returns an array of amounts converted from the original (`from`) currency to the target (`to`) currency and the timestamp for the conversion rate used.
   *
   *
   * Use the `convertCurrency()` function to convert an array of one or more amounts between two currencies. The `convertCurrency()` function returns an array of converted amounts and the timestamp for the conversion rate used.
   *
   * > **Note**:  The currency codes used must exist in the array of supported currencies returned by the [`listCurrencies()`](#listcurrencies) function.
   *
   * @param - Amounts to convert.
   * @param - Identifying details needed to determine which currency rate to convert. The combination of the `from` and `to` properties together comprise the unique ID.
   * @param - Options to use when converting currency.
   */
  (
    identifiers: NonNullablePaths<ConvertCurrencyIdentifiers, `from` | `to`, 2>,
    amounts: DecimalValue[]
  ): Promise<
    NonNullablePaths<
      ConvertCurrencyResponse,
      `amounts` | `amounts.${number}.value` | `amounts.${number}.decimalPlaces`,
      4
    >
  >;
}

export function getConversionRate(
  httpClient: HttpClient
): GetConversionRateSignature {
  return (
    identifiers: NonNullablePaths<
      GetConversionRateIdentifiers,
      `from` | `to`,
      2
    >
  ) =>
    universalGetConversionRate(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface GetConversionRateSignature {
  /**
   * Returns the conversion rate between 2 currencies.
   * @param - Identifying details needed to get the conversion rate. The combination of the `from` and `to` properties together comprise the unique ID.
   */
  (
    identifiers: NonNullablePaths<
      GetConversionRateIdentifiers,
      `from` | `to`,
      2
    >
  ): Promise<
    NonNullablePaths<
      ConversionRateResponse,
      `rate.value` | `rate.decimalPlaces`,
      3
    >
  >;
}

export {
  ConversionRateRequest,
  ConversionRateResponse,
  ConvertCurrencyIdentifiers,
  ConvertCurrencyRequest,
  ConvertCurrencyResponse,
  Currency,
  CurrencyRate,
  DecimalValue,
  GetConversionRateIdentifiers,
  ListCurrenciesRequest,
  ListCurrenciesResponse,
} from './ecommerce-currency-converter-v1-currency-rate-currencies.universal.js';
